import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate } from '../utils/dateConverter';
import {
    STATUS_LABEL,
    barGeometry,
    brokenDependencies,
    dragDates,
    daysFromFraction,
    isUnchanged,
    monthBands,
    timelineRange,
    toDate,
    toIso,
} from '../utils/planning';

const pct = (fraction) => `${(fraction * 100).toFixed(3)}%`;

/** A Gantt for one project; `onReschedule(task, dates)` persists a dragged bar. */
export default function PlanningTimeline({ tasks, onTaskClick, onReschedule }) {
    const scheduled = (tasks || []).filter(t => t.planned_start);
    const unscheduled = (tasks || []).filter(t => !t.planned_start);
    const range = timelineRange(scheduled);
    const broken = brokenDependencies(tasks);
    const brokenIds = new Set(broken.map(b => String(b.task.id)));

    // The drag lives in a ref, not only in state: `pointerup` can arrive in the same batch
    // as the last `pointermove`, and a ref read during render would still say `moved: false`.
    const [drag, setDrag] = useState(null);
    const dragRef = useRef(null);

    const beginDrag = (event, task, mode) => {
        if (!onReschedule || (task.is_milestone && mode !== 'move')) return;
        event.preventDefault();
        event.stopPropagation();
        const track = event.currentTarget.closest('.pl-tl-track');
        const trackWidth = track ? track.getBoundingClientRect().width : 0;
        // A collapsed track would turn the pointer offset into Infinity days.
        if (!trackWidth) return;
        dragRef.current = {
            id: String(task.id),
            task,
            mode,
            range,
            originX: event.clientX,
            trackWidth,
            days: 0,
            moved: false,
        };
        setDrag(dragRef.current);
    };

    const handleMove = useCallback((event) => {
        const cur = dragRef.current;
        if (!cur) return;
        const days = daysFromFraction((event.clientX - cur.originX) / cur.trackWidth, cur.range);
        if (days === cur.days) return;
        dragRef.current = { ...cur, days, moved: true };
        setDrag(dragRef.current);
    }, []);

    const handleUp = useCallback(() => {
        const cur = dragRef.current;
        dragRef.current = null;
        setDrag(null);
        if (!cur || !cur.moved) return;
        const dates = dragDates(cur.task, cur.mode, cur.days);
        if (!dates || isUnchanged(cur.task, dates)) return;
        onReschedule(cur.task, dates);
    }, [onReschedule]);

    const dragActive = !!drag;
    useEffect(() => {
        if (!dragActive) return undefined;
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
        };
    }, [dragActive, handleMove, handleUp]);

    const today = toIso(new Date());
    const todayGeom = range && toDate(today) >= range.start && toDate(today) <= range.end
        ? barGeometry({ planned_start: today, planned_end: today }, range)
        : null;

    /** The bar's geometry, following the cursor while this task is the one being dragged. */
    const geometryFor = (task) => {
        const live = drag && drag.id === String(task.id) && drag.moved
            ? { ...task, ...dragDates(task, drag.mode, drag.days) }
            : task;
        return { geom: barGeometry(live, range), preview: live };
    };

    return (
        <div className="pl-timeline">
            {!range && (
                <p className="pl-empty">
                    Noch nichts terminiert. Sobald eine Aufgabe ein Start- und Enddatum hat,
                    erscheint sie hier.
                </p>
            )}

            {range && (
                <>
                    <div className="pl-tl-head">
                        <div className="pl-tl-label" />
                        <div className="pl-tl-track">
                            {monthBands(range).map(band => (
                                <span
                                    key={band.key}
                                    className="pl-tl-month"
                                    style={{ left: pct(band.left), width: pct(band.width) }}
                                >
                                    {band.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="pl-tl-rows">
                        {scheduled.map(task => {
                            const { geom, preview } = geometryFor(task);
                            if (!geom) return null;
                            const dragging = drag && drag.id === String(task.id) && drag.moved;
                            return (
                                <div className="pl-tl-row" key={task.id}>
                                    <button
                                        type="button"
                                        className="pl-tl-label pl-tl-label--button"
                                        onClick={() => onTaskClick(task)}
                                        title={task.title || 'Ohne Titel'}
                                    >
                                        {task.is_milestone && <span className="pl-milestone">◆</span>}
                                        {task.title || 'Ohne Titel'}
                                    </button>
                                    <div className="pl-tl-track">
                                        {todayGeom && (
                                            <span
                                                className="pl-tl-today"
                                                style={{ left: pct(todayGeom.left) }}
                                                aria-hidden="true"
                                            />
                                        )}
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            className={[
                                                'pl-tl-bar',
                                                `pl-tl-bar--${task.status}`,
                                                task.is_milestone ? 'pl-tl-bar--milestone' : '',
                                                brokenIds.has(String(task.id)) ? 'pl-tl-bar--broken' : '',
                                                dragging ? 'pl-tl-bar--dragging' : '',
                                            ].filter(Boolean).join(' ')}
                                            style={{ left: pct(geom.left), width: pct(geom.width) }}
                                            data-testid={`bar-${task.id}`}
                                            onPointerDown={e => beginDrag(e, task, 'move')}
                                            onClick={() => { if (!drag) onTaskClick(task); }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    onTaskClick(task);
                                                }
                                            }}
                                            title={[
                                                task.title,
                                                `${formatDate(task.planned_start)} – ${formatDate(task.planned_end || task.planned_start)}`,
                                                `${geom.days} Tag${geom.days === 1 ? '' : 'e'}`,
                                                STATUS_LABEL[task.status],
                                                task.assignee_name || 'niemand zugewiesen',
                                                onReschedule ? 'Ziehen zum Verschieben, Ränder zum Verlängern' : null,
                                            ].filter(Boolean).join(' · ')}
                                        >
                                            {dragging && (
                                                <span className="pl-tl-ghost">
                                                    {formatDate(preview.planned_start)} – {formatDate(preview.planned_end)}
                                                </span>
                                            )}
                                            {!task.is_milestone && (
                                                <span className="pl-tl-bar-text">
                                                    {task.assignee_name || '—'}
                                                    {task.estimate_hours > 0 && ` · ${task.estimate_hours} h`}
                                                </span>
                                            )}
                                            {onReschedule && !task.is_milestone && (
                                                <>
                                                    <span
                                                        className="pl-tl-handle pl-tl-handle--start"
                                                        data-testid={`handle-start-${task.id}`}
                                                        onPointerDown={e => beginDrag(e, task, 'start')}
                                                        aria-hidden="true"
                                                    />
                                                    <span
                                                        className="pl-tl-handle pl-tl-handle--end"
                                                        data-testid={`handle-end-${task.id}`}
                                                        onPointerDown={e => beginDrag(e, task, 'end')}
                                                        aria-hidden="true"
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {broken.length > 0 && (
                <div className="pl-warnings" role="status">
                    <strong>Terminkonflikt:</strong>
                    <ul>
                        {broken.map(({ task, parent }) => (
                            <li key={task.id}>
                                „{task.title}" startet am {formatDate(task.planned_start)}, aber
                                „{parent.title}" endet erst am{' '}
                                {formatDate(parent.planned_end || parent.planned_start)}.
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {unscheduled.length > 0 && (
                <div className="pl-unscheduled">
                    <div className="pl-section-title">
                        Nicht terminiert ({unscheduled.length})
                    </div>
                    <ul className="pl-unscheduled-list">
                        {unscheduled.map(task => (
                            <li key={task.id}>
                                <button type="button" onClick={() => onTaskClick(task)}>
                                    {task.title || 'Ohne Titel'}
                                </button>
                                <span className="pl-unscheduled-meta">
                                    {task.assignee_name || 'niemand'}
                                    {task.estimate_hours > 0 && ` · ${task.estimate_hours} h`}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
